import {
  BeneficialOwnerFormType,
  BusinessDetailsType,
  ContactDetailsType,
  EligibilityType,
  FinancePurchaseType,
  GuarantorDetailsFormType,
  HouseholdType,
  IdentityType,
  IncomeReviewFieldsType,
  IncomeVerificationType,
  OwnershipDetailsType,
  RepaymentAccountType,
  SummaryType,
} from './';

export enum AddressState {
  ACT = 'ACT',
  NSW = 'NSW',
  NT = 'NT',
  QLD = 'QLD',
  SA = 'SA',
  TAS = 'TAS',
  VIC = 'VIC',
  WA = 'WA',
}

export type AddressType = {
  unitNumber?: string;
  streetNumber: string;
  streetName: string;
  suburb: string;
  state: AddressState;
  postcode: string;
  dwellingType?: string;
  occupancyStatus?: string;
};

export type repaymentOptions = {
  term: number;
  amount: number;
  termNumberOfPayments: number;
  paymentRegularFirstDate: string;
  paymentCountRegular: number;
  interestCycle: string;
  paymentCycle: string;
  totalInterest: number;
  totalPayments: number;
  dateMaturity: string;
  interestRate: number;
};

export enum ApplicantStatus {
  Draft = 'DRAFT',
  Submitted = 'SUBMITTED',
  Specified = 'SPECIFIED',
  Declined = 'DECLINED',
  Accepted = 'ACCEPTED',
  Approved = 'APPROVED',
}

export enum ApplicationFormStatus {
  Draft = 'DRAFT',
  Submitted = 'SUBMITTED',
  Approved = 'APPROVED',
  Declined = 'DECLINED',
  Withdrawn = 'WITHDRAWN',
}

export enum ApplicationFormSubStatus {
  PendingAcceptance = 'PEND.ACPT',
  Accepted = 'ACCEPTED',
  Approved = 'APPROVED',
  PendingAdditionalIncomeDocuments = 'PEND.ADDIN',
  HoldJoint = 'HOLD.JOINT',
}

export enum MedicareCardColour {
  Blue = 'Blue',
  Green = 'Green',
  Yellow = 'Yellow',
}

export enum MedicareType {
  Yellow = 'YELLOW',
  Green = 'GREEN',
  Blue = 'BLUE',
}

export enum EmploymentType {
  SELF_EMPLOYED = 'self-employed',
  EMPLOYED = 'employed',
  PENSIONER = 'pensioner',
  OTHER = 'other',
}

export enum PensionType {
  DISABILITY = 'DISABILITY',
  PENSIONER = 'PENSIONER',
  SELF_FUNDED_RETIREE = 'SELF_FUNDED_RETIREE',
  VETERAN = 'VETERAN',
  CARERS_PENSION = 'CARERS_PENSION',
}

export enum Period {
  Fortnightly = 'Fortnightly',
  Monthly = 'Monthly',
  Weekly = 'Weekly',
  Annually = 'Annually',
}

export enum IdentificationType {
  DRIVING_LICENSE = 'DRIVING_LICENSE',
  MEDICARE = 'MEDICARE',
  PASSPORT = 'PASSPORT',
  PHOTO_ID = 'PHOTO_ID',
}

export enum IdentityVerificationOverallOutcome {
  ACCEPT = 'ACCEPT',
  REJECT = 'REJECT',
  TIMEOUT = 'TIMEOUT',
  ERROR = 'ERROR',
}

export enum ApplicantType {
  Primary = 'Primary',
  Joint = 'Joint',
}

export enum ApplicationStep {
  AddJointApplicant = 'AddJointApplicant',
  BeneficialOwner = 'BeneficialOwner',
  BusinessDetails = 'BusinessDetails',
  CreateApplication = 'CreateApplication',
  GuarantorDetails = 'GuarantorDetails',
  HouseholdDetails = 'HouseholdDetails',
  Identification = 'Identification',
  IncomeVerification = 'IncomeVerification',
  JointEligibility = 'JointEligibility',
  OwnershipDetails = 'OwnershipDetails',
  PaymentDetails = 'PaymentDetails',
  SubmitApplication = 'SubmitApplication',
}

export type ApplicationFormType = {
  Finance: FinancePurchaseType;
  Eligibility: EligibilityType;
  Identification: IdentityType;
  IncomeVerification: IncomeVerificationType;
  HouseholdDetails: HouseholdType;
  PaymentDetails: RepaymentAccountType;
  SubmitApplication: SummaryType;
  ContactDetails: ContactDetailsType;
  BusinessDetails: BusinessDetailsType;
  OwnershipDetails: OwnershipDetailsType;
  BeneficialOwner: BeneficialOwnerFormType;
  IncomeReview: IncomeReviewFieldsType;
  GuarantorDetails: GuarantorDetailsFormType;
};

export type ApplicationFormValues = ApplicationFormType[keyof ApplicationFormType];

export enum EnterpriseStructure {
  Company = 'Company',
  IncorporatedAssociation = 'Incorporated Association',
  UnincorporatedAssociation = 'Unincorporated Association',
  Partnership = 'Partnership',
  ProprietaryLimitedCompany = 'Proprietary Limited Company',
  Trust = 'Trust',
  SoleTrader = 'Sole Trader',
  Other = 'Other',
}

const EXTENDED_STRUCTURES: EnterpriseStructure[] = [
  EnterpriseStructure.ProprietaryLimitedCompany,
  EnterpriseStructure.Partnership,
  EnterpriseStructure.Trust,
  EnterpriseStructure.SoleTrader,
  EnterpriseStructure.Other,
];
export type EntityStructureOption = {
  value: EnterpriseStructure;
  label: string;
};

export const isExtendedStructure = (structure: EnterpriseStructure): boolean => EXTENDED_STRUCTURES.includes(structure);

export enum OwnershipType {
  Proprietary = 'Proprietary',
  Public = 'Public',
  Individual = 'Individual',
  Corporate = 'Corporate',
}

export enum CustomerType {
  Business = 'Business',
  Residential = 'Residential',
}

export enum LoanType {
  Loan = 'Loan',
  PaymentPlan = 'PaymentPlan',
}

export enum VerificationType {
  SmsEmail = 'SmsEmail',
  Sms = 'Sms',
  Email = 'Email',
}

// Application document entity type
// use to define which entity type the uploaded document belongs to
export enum ApplicationEntityType {
  Application = 'Application',
  Primary = 'Primary',
  Joint = 'Joint',
}

export enum ApplicationDocumentType {
  Identification = 'Identification',
  IncomeVerification = 'IncomeVerification',
  Others = 'Others',
}

export enum IncomeDocumentVerificationStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

/**
 * Canonical income-document types. String values match the labels the AI
 * classifier emits and that are persisted in the `documentClassification` JSON,
 * so a customer-declared type routes to the same prompt as a classified one.
 *
 * `Tax Assessment` is the taxpayer's lodged individual income tax return (the UI
 * surfaces it as "Income Tax Return"); `Notice Of Assessment` is the separate
 * ATO-issued assessment.
 */
export enum IncomeDocumentType {
  Payslip = 'Payslip',
  BankStatement = 'Bank Statement',
  NoticeOfAssessment = 'Notice Of Assessment',
  TaxAssessment = 'Tax Assessment',
  PensionStatement = 'Pension Statement',
  CentrelinkStatement = 'Centrelink Statement',
  SuperannuationStatement = 'Superannuation Statement',
  Others = 'Others',
  NotReadable = 'Not Readable',
}

/**
 * The document types a customer may select at upload — the single source of truth
 * for the accepted-type allow-list. `Others` / `Not Readable` are classifier-only
 * outcomes and are intentionally excluded. Kept in sync with the registry's wired
 * processors (enforced by incomeDocumentProcessorRegistry.spec.ts).
 */
export const SUPPORTED_INCOME_DOCUMENT_TYPES = [
  IncomeDocumentType.Payslip,
  IncomeDocumentType.BankStatement,
  IncomeDocumentType.NoticeOfAssessment,
  IncomeDocumentType.TaxAssessment,
  IncomeDocumentType.PensionStatement,
  IncomeDocumentType.CentrelinkStatement,
  IncomeDocumentType.SuperannuationStatement,
] as const;

export enum DocumentStatus {
  New = 'New',
  Submitted = 'Submitted',
  Deleted = 'Deleted',
  Uploaded = 'Uploaded',
}

export enum DocumentRequestStatus {
  New = 'New',
  Submitted = 'Submitted',
  Expired = 'Expired',
}

/**
 * What a post-submission document request is asking for. One field, not a type plus a
 * subtype: the income slot is part of the answer, not a subdivision of it.
 *
 * Persisted in `ApplicationDocumentRequest.type`, which is nullable — rows from the
 * pre-existing single-request endpoint carry no type and sit outside these rules.
 *
 * Name and value are identical, unlike `IncomeDocumentType` whose members map spaceless
 * names to spaced values, so the GraphQL wire form and the stored form are one string.
 */
export enum DocumentRequestType {
  /** Evidence for `Applicant.data.income.primary`. */
  PrimaryIncome = 'PrimaryIncome',
  /** Evidence for `Applicant.data.income.additional`. */
  AdditionalIncome = 'AdditionalIncome',
  /** Supporting evidence that carries no income. */
  Others = 'Others',
}

/**
 * The `ApplicationDocumentType` a document uploaded against each request is filed under.
 * Both income requests collapse to `IncomeVerification`, which is the type the existing
 * income pipeline already keys on — the request type keeps the finer distinction.
 */
export const DOCUMENT_REQUEST_TYPE_TO_DOCUMENT_TYPE: Record<DocumentRequestType, ApplicationDocumentType> = {
  [DocumentRequestType.PrimaryIncome]: ApplicationDocumentType.IncomeVerification,
  [DocumentRequestType.AdditionalIncome]: ApplicationDocumentType.IncomeVerification,
  [DocumentRequestType.Others]: ApplicationDocumentType.Others,
};

/** The `Applicant.data.income` slot a request feeds, or null when it carries no income. */
export const DOCUMENT_REQUEST_TYPE_TO_INCOME_SLOT: Record<DocumentRequestType, 'primary' | 'additional' | null> = {
  [DocumentRequestType.PrimaryIncome]: 'primary',
  [DocumentRequestType.AdditionalIncome]: 'additional',
  [DocumentRequestType.Others]: null,
};

export const isIncomeDocumentRequest = (type: DocumentRequestType | string | null): boolean =>
  type === DocumentRequestType.PrimaryIncome || type === DocumentRequestType.AdditionalIncome;

/**
 * The specific document Operations can ask for. Optional on a request: leaving it unset
 * means the customer chooses.
 *
 * Separate from `IncomeDocumentType` on purpose. Name and value are identical here, so
 * there is no wire-versus-stored split to trip over, and the list can carry supporting
 * documents that are not income at all.
 */
export enum RequestedDocumentType {
  Payslip = 'Payslip',
  BankStatement = 'BankStatement',
  NoticeOfAssessment = 'NoticeOfAssessment',
  TaxAssessment = 'TaxAssessment',
  PensionStatement = 'PensionStatement',
  CentrelinkStatement = 'CentrelinkStatement',
  SuperannuationStatement = 'SuperannuationStatement',
  Others = 'Others',
}

export const REQUESTED_TO_INCOME_DOCUMENT_TYPE: Record<RequestedDocumentType, IncomeDocumentType> = {
  [RequestedDocumentType.Payslip]: IncomeDocumentType.Payslip,
  [RequestedDocumentType.BankStatement]: IncomeDocumentType.BankStatement,
  [RequestedDocumentType.NoticeOfAssessment]: IncomeDocumentType.NoticeOfAssessment,
  [RequestedDocumentType.TaxAssessment]: IncomeDocumentType.TaxAssessment,
  [RequestedDocumentType.PensionStatement]: IncomeDocumentType.PensionStatement,
  [RequestedDocumentType.CentrelinkStatement]: IncomeDocumentType.CentrelinkStatement,
  [RequestedDocumentType.SuperannuationStatement]: IncomeDocumentType.SuperannuationStatement,
  [RequestedDocumentType.Others]: IncomeDocumentType.Others,
};

export enum DocumentS3FileStatus {
  Exists = 'Exists',
  Deleted = 'Deleted',
}

export enum PaymentFunds {
  CurrentSavings = 'Current savings',
  MortgageRedraw = 'Mortgage redraw',
  FutureEarnings = 'Future earnings',
}

export enum RepaymentType {
  DirectDebit = 'DEBIT',
  CreditCard = 'CARD',
}

export enum IncomeCategory {
  INCOME_SALARY = 'INCOME_SALARY',
  INCOME_INVESTMENTS = 'INCOME_INVESTMENTS',
  INCOME_CHILD_SUPPORT = 'INCOME_CHILD_SUPPORT',
  INCOME_RENTAL = 'INCOME_RENTAL',
  INCOME_GOVERNMENT_BENEFITS = 'INCOME_GOVERNMENT_BENEFITS',
}

export enum PaymentFrequency {
  WEEKLY = 'weekly',
  FORTNIGHTLY = 'fortnightly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  IRREGULAR = 'irregular',
  UNDETERMINED = 'undetermined',
}

export enum OpenBankingFeedBack {
  PROBLEM_LOGGING_IN = 'PROBLEM_LOGGING_IN',
  PREFER_MANUAL = 'PREFER_MANUAL',
  TOO_COMPLICATED = 'TOO_COMPLICATED',
  OTHER = 'OTHER',
}

export enum FeatureFlags {
  OpenBankingMultipleConsents = 'open-banking-multiple-consents',
  MultiDocumentRequests = 'multi-document-requests',
}

// CT-3015: per-owner income type, captured in step 1 (updateTitleSearchOwner).
export enum TitleOwnerIncomeType {
  EMPLOYED = 'EMPLOYED',
  SELF_EMPLOYED = 'SELF_EMPLOYED',
  RETIREE = 'RETIREE',
  PENSIONER = 'PENSIONER',
  NO_INCOME = 'NO_INCOME',
}

// CT-3015: which verification check the owner used, set at verify time.
export enum TitleOwnerVerificationMethod {
  NOA = 'NOA',
  SUPERANNUATION = 'SUPERANNUATION',
  IDMATRIX = 'IDMATRIX',
  NO_INCOME = 'NO_INCOME',
  NONE = 'NONE',
}

// CT-3015: PENDING after step 1; VERIFIED | FAILED after verify.
export enum TitleOwnerVerificationStatus {
  PENDING = 'PENDING',
  VERIFIED = 'VERIFIED',
  FAILED = 'FAILED',
}

// Per-owner reason a title owner's income verification landed where it did,
// recorded on incomeVerificationDetails.reasonCode and forwarded to finPOWER
// alongside the household eligibility status. Orthogonal to eligibility: a name
// mismatch stays VERIFIED / Pass but still carries NAME_MISMATCH here. Absent /
// null means a clean verification with nothing to flag.
export enum TitleOwnerVerificationReasonCode {
  MANUALLY_ADDED = 'MANUALLY_ADDED',
  // Verified, but the NOA taxpayer name did not match the registered title holder.
  NAME_MISMATCH = 'NAME_MISMATCH',
  // Readable NOA reporting $0 taxable income (non-pensioner).
  INCOME_ZERO = 'INCOME_ZERO',
  // NOA present but the taxable-income figure could not be read.
  DOCUMENT_UNREADABLE = 'DOCUMENT_UNREADABLE',
  // Uploaded document was not a Notice of Assessment.
  DOCUMENT_TYPE_MISMATCH = 'DOCUMENT_TYPE_MISMATCH',
  // NOA financial year is outside the acceptable window.
  NOTICE_OF_ASSESSMENT_OUT_OF_DATE = 'NOTICE_OF_ASSESSMENT_OUT_OF_DATE',
  // A mix of unreadable and wrong-type documents was uploaded.
  MULTIPLE_DOCUMENT_TYPE_FAILED = 'MULTIPLE_DOCUMENT_TYPE_FAILED',
  // Legacy value retained for records created by the former observe-only superannuation flow.
  SUPERANNUATION_UNVERIFIED = 'SUPERANNUATION_UNVERIFIED',
  // Pensioner concession-card IDMatrix check did not ACCEPT (reject / error / cap).
  IDMATRIX_REJECTED = 'IDMATRIX_REJECTED',
  // Reached submit without a completed verification (non-pensioner NONE, or a
  // defensive pensioner who never attempted a check).
  NOT_VERIFIED = 'NOT_VERIFIED',
}

export enum IncomeVerificationInput {
  Failed = 'Failed',
}

export enum IncomeVerificationStatus {
  Pending = 'Pending',
  Passed = 'Passed',
  Failed = 'Failed',
}
